import { useState, useEffect } from 'react';
import { apiClient } from '@services/api';
import { connect } from 'react-redux';
import CircularProgress from '@material-ui/core/CircularProgress';

const PERIODS = [
  { label: '7 dias',  value: '7' },
  { label: '30 dias', value: '30' },
  { label: '90 dias', value: '90' },
  { label: 'Todo',    value: null },
];

const EMOJIS = { 1: '😠', 2: '😕', 3: '😐', 4: '🙂', 5: '😄' };

const EMOJI_COLORS = {
  1: '#ef4444',
  2: '#f97316',
  3: '#eab308',
  4: '#84cc16',
  5: '#22c55e',
};

const Csat = (props) => {
  const [days, setDays]     = useState('30');
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { props.setShowMenu(false); }, []);

  useEffect(() => {
    setLoading(true);
    apiClient.httpInstance
      .get('/tickets/csat', { params: days ? { days } : {} })
      .then(res => { if (res.status === 200) setData(res.data); })
      .finally(() => setLoading(false));
  }, [days]);

  return (
    <div className="px-7 pb-10 max-w-5xl">
      {/* ── Cabeçalho ── */}
      <div className="mt-2 mb-1">
        <h1 className="text-2xl font-bold text-white">⭐ Satisfação do suporte</h1>
        <p className="text-sm mt-1" style={{ color: '#64748b' }}>
          Avaliações deixadas pela equipe ao fechar um ticket (1–5 ⭐).
          Dados são úteis a partir de ~15 avaliações.
        </p>
      </div>

      {/* ── Filtros de período ── */}
      <div className="flex gap-2 mt-4 mb-6">
        {PERIODS.map(p => (
          <button
            key={p.value ?? 'all'}
            onClick={() => setDays(p.value)}
            className="px-4 py-1.5 rounded-lg text-sm transition-all"
            style={{
              background: days === p.value ? props.theme.color + '33' : '#0f2340',
              border: days === p.value
                ? `1px solid ${props.theme.color}`
                : '1px solid #1e3a5f',
              color: days === p.value ? '#fff' : '#94a3b8',
              cursor: 'pointer',
            }}
          >
            {p.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-24">
          <CircularProgress style={{ color: props.theme.color }} />
        </div>
      ) : !data || data.total === 0 ? (
        <EmptyState />
      ) : (
        <>
          {/* ── Resumo KPIs ── */}
          <Section title="📊 Resumo">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <KpiCard
                label="PROMÉDIO"
                value={<><span style={{ color: '#22c55e', fontSize: 28, fontWeight: 700 }}>{data.average?.toFixed(2)}</span><span style={{ color: '#64748b', fontSize: 14 }}> / 5</span></>}
              />
              <KpiCard
                label="% BAIXAS (≤2)"
                value={<span style={{ color: data.low_pct > 10 ? '#ef4444' : '#22c55e', fontSize: 28, fontWeight: 700 }}>{data.low_pct}%</span>}
              />
              <KpiCard
                label="TOTAL AVALIADOS"
                value={<span style={{ color: '#e2e8f0', fontSize: 28, fontWeight: 700 }}>{data.total}</span>}
              />
              <KpiCard
                label="1ª RESPOSTA (h)"
                value={
                  data.avg_first_reply
                    ? <span style={{ color: '#e2e8f0', fontSize: 28, fontWeight: 700 }}>{data.avg_first_reply}h</span>
                    : <span style={{ color: '#64748b', fontSize: 20 }}>—</span>
                }
              />
            </div>
          </Section>

          {/* ── Distribuição + Tendência ── */}
          <div className="grid grid-cols-1 gap-4 mt-4 sm:grid-cols-2">
            {/* Distribuição */}
            <Section title="📊 Distribuição">
              <DistBar distribution={data.distribution} total={data.total} />
            </Section>

            {/* Tendência */}
            <Section title="📈 Tendência (média por semana)">
              <TrendChart weeks={data.week_trend} themeColor={props.theme.color} />
            </Section>
          </div>

          {/* ── Por responsável ── */}
          {data.by_assignee?.length > 0 && (
            <Section title="👤 Por responsável" className="mt-4">
              <div className="divide-y" style={{ borderColor: '#1e3a5f' }}>
                {data.by_assignee.map((a, i) => (
                  <div key={i} className="flex items-center justify-between py-3">
                    <div>
                      <p className="text-sm font-medium text-white">{a.assignee_name}</p>
                      <p className="text-xs mt-0.5" style={{ color: '#64748b' }}>{a.total} avaliações</p>
                    </div>
                    <div className="flex items-center gap-4">
                      {a.low_count > 0 && (
                        <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: '#7f1d1d33', color: '#ef4444' }}>
                          ⚠️ {a.low_count} baixas
                        </span>
                      )}
                      <span className="text-lg font-bold" style={{ color: avgColor(a.average) }}>
                        {a.average?.toFixed(2)}
                      </span>
                      <span style={{ fontSize: 22 }}>{avgEmoji(a.average)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* ── Avaliações baixas ── */}
          <Section
            title={`⚠️ Avaliações baixas (${data.low_count})`}
            className="mt-4"
            titleColor={data.low_count > 0 ? '#f59e0b' : undefined}
          >
            {data.low_ratings?.length === 0 ? (
              <p className="text-sm py-4 text-center" style={{ color: '#64748b' }}>
                ✨ Nenhuma avaliação baixa neste período. Continue assim!
              </p>
            ) : (
              <div className="space-y-3">
                {data.low_ratings.map(r => (
                  <LowRatingCard key={r.id} rating={r} />
                ))}
              </div>
            )}
          </Section>

          {!data.enough_data && (
            <p className="text-xs mt-4 text-center" style={{ color: '#475569' }}>
              ℹ️ Dados estatisticamente relevantes a partir de 15 avaliações. Você tem {data.total}.
            </p>
          )}
        </>
      )}
    </div>
  );
};

// ── Sub-componentes ────────────────────────────────────────────

const Section = ({ title, children, className = '', titleColor }) => (
  <div
    className={`p-5 rounded-xl ${className}`}
    style={{ background: '#0f2340', border: '1px solid #1e3a5f' }}
  >
    <h2 className="text-sm font-semibold mb-4" style={{ color: titleColor || '#94a3b8' }}>
      {title}
    </h2>
    {children}
  </div>
);

const KpiCard = ({ label, value }) => (
  <div
    className="p-4 rounded-xl"
    style={{ background: '#0a1929', border: '1px solid #1e3a5f' }}
  >
    <p className="text-xs tracking-wider mb-2" style={{ color: '#64748b' }}>{label}</p>
    <div>{value}</div>
  </div>
);

const DistBar = ({ distribution, total }) => {
  const max = Math.max(...Object.values(distribution), 1);
  return (
    <div className="flex items-end justify-around gap-2" style={{ height: 120 }}>
      {[1,2,3,4,5].map(v => {
        const count = distribution[v] || 0;
        const pct   = Math.round((count / max) * 100);
        return (
          <div key={v} className="flex flex-col items-center gap-1 flex-1">
            <span className="text-xs" style={{ color: '#94a3b8' }}>{count}</span>
            <div
              className="w-full rounded-t-lg transition-all"
              style={{
                height: `${Math.max(pct, 4)}%`,
                background: count > 0 ? EMOJI_COLORS[v] : '#1e3a5f',
                minHeight: 6,
              }}
            />
            <span style={{ fontSize: 20 }}>{EMOJIS[v]}</span>
            <span className="text-xs" style={{ color: '#64748b' }}>{v}</span>
          </div>
        );
      })}
    </div>
  );
};

const TrendChart = ({ weeks, themeColor }) => {
  if (!weeks || weeks.length < 2) {
    return (
      <div className="flex items-center justify-center py-8">
        <p className="text-sm text-center italic" style={{ color: '#475569' }}>
          Precisamos de pelo menos 2 semanas com avaliações para mostrar a tendência.
        </p>
      </div>
    );
  }

  const max = 5;
  const min = 1;
  const W = 300;
  const H = 100;
  const padX = 10;
  const padY = 10;

  const points = weeks.map((w, i) => {
    const x = padX + (i / (weeks.length - 1)) * (W - padX * 2);
    const y = H - padY - ((w.average - min) / (max - min)) * (H - padY * 2);
    return { x, y, ...w };
  });

  const path = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`)
    .join(' ');

  return (
    <div className="w-full overflow-x-auto">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ minWidth: 200 }}>
        {/* Linhas de referência */}
        {[1,2,3,4,5].map(v => {
          const y = H - padY - ((v - min) / (max - min)) * (H - padY * 2);
          return (
            <line key={v} x1={padX} y1={y} x2={W - padX} y2={y}
              stroke="#1e3a5f" strokeWidth="0.5" strokeDasharray="3,3" />
          );
        })}
        {/* Linha de tendência */}
        <path d={path} fill="none" stroke={themeColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        {/* Pontos */}
        {points.map((p, i) => (
          <g key={i}>
            <circle cx={p.x} cy={p.y} r={4} fill={themeColor} />
            <title>{p.week}: {p.average} ({p.total} aval.)</title>
          </g>
        ))}
      </svg>
      <div className="flex justify-between text-xs mt-1" style={{ color: '#475569' }}>
        <span>{weeks[0]?.week}</span>
        <span>{weeks[weeks.length - 1]?.week}</span>
      </div>
    </div>
  );
};

const LowRatingCard = ({ rating: r }) => (
  <div
    className="p-3 rounded-lg"
    style={{ background: '#1a070733', border: '1px solid #7f1d1d55' }}
  >
    <div className="flex items-start justify-between gap-3">
      <div className="flex-1 min-w-0">
        <p className="text-sm text-white truncate">#{r.id} — {r.subject}</p>
        <p className="text-xs mt-0.5" style={{ color: '#64748b' }}>
          {r.opener_name} → {r.assignee_name} · {r.rated_at}
        </p>
        {r.rating_comment && (
          <p className="text-xs mt-2 italic" style={{ color: '#94a3b8' }}>
            "{r.rating_comment}"
          </p>
        )}
      </div>
      <span style={{ fontSize: 28, flexShrink: 0 }}>{r.rating_emoji}</span>
    </div>
  </div>
);

const EmptyState = () => (
  <div
    className="flex flex-col items-center justify-center py-24 rounded-xl"
    style={{ background: '#0f2340', border: '1px solid #1e3a5f' }}
  >
    <span style={{ fontSize: 48 }}>⭐</span>
    <p className="mt-3 text-base font-medium text-white">Ainda não há avaliações</p>
    <p className="mt-1 text-sm text-center max-w-xs" style={{ color: '#64748b' }}>
      Os dados aparecerão automaticamente quando a equipe começar a avaliar os tickets resolvidos.
      Você pode mudar o filtro de período acima para um intervalo mais amplo.
    </p>
  </div>
);

// helpers
const avgColor = (avg) => {
  if (avg >= 4) return '#22c55e';
  if (avg >= 3) return '#eab308';
  return '#ef4444';
};

const avgEmoji = (avg) => {
  if (avg >= 4.5) return '😄';
  if (avg >= 3.5) return '🙂';
  if (avg >= 2.5) return '😐';
  if (avg >= 1.5) return '😕';
  return '😠';
};

const mapStateToProps = state => ({ user: state.user });
export default connect(mapStateToProps)(Csat);
